// lib
import React, { useContext } from 'react'

// components
import { TabItemLink } from 'src/components/tabs/tab_item'
import Tabs from 'src/components/tabs/tabs'

// constants/interfaces
import { CLASS_PREFIX } from 'src/constants'
import {
  FEED_TYPE_EVENT,
  FEED_TYPE_LOCAL,
  FEED_TYPE_NEWS,
  INFRASTRUCTURE_PROJECTS,
  PUBLIC_PARTICIPATION
} from 'src/constants/feed'
import {
  GROUP_OVERVIEW,
  NOTIFICATIONS,
  PROFILES_DIRECTORY
} from 'src/constants/navigation'
import {
  FRONTEND_GROUP_OVERVIEW_PATH,
  FRONTEND_NOTIFICATIONS_PATH
} from 'src/constants/urls'
import { URLContext } from 'src/containers/url_provider/url_provider'
import { textResources } from 'src/lang/de'

// helpers
import initTracker, {
  NAVIGATION
} from 'src/utils/reporting/events_tracking/events_tracking'

import { useFeatureFlags } from '@lopo-tech/frontends-shared'
import { useGlobalPermissions } from '@lopo-tech/lopo-api'
import { FeatureFlag } from 'src/components/feature_flag/feature_flag'
import { InfrastructureProjectsNavigationIcon } from 'src/components/icons/projects/infrastructure/infrastructure-projects-navigation-icon'
import { PublicParticipationNavigationIcon } from 'src/components/icons/projects/public-participation/public-participation-navigation-icon'
import { usePollUnseenNotificationsCount } from 'src/hooks/notification_hooks'
import './header_navigation.scss'

const cls: string = CLASS_PREFIX + 'header-navigation'

export type ActivableNavigationItems =
  | typeof FEED_TYPE_LOCAL
  | typeof FEED_TYPE_NEWS
  | typeof FEED_TYPE_EVENT
  | typeof PUBLIC_PARTICIPATION
  | typeof INFRASTRUCTURE_PROJECTS
  | typeof NOTIFICATIONS
  | typeof GROUP_OVERVIEW
  | typeof PROFILES_DIRECTORY;

interface IProps {
  activeNavigationItem?: ActivableNavigationItems;
  markAllNotificationsAsSeen?: () => void;
  onClick?: () => void;
  withSubheader?: boolean;
}

const tracker = initTracker(NAVIGATION)

const clickEvent = () => {
  if (window.scrollY !== 0) {
    window.scroll({
      behavior: 'smooth',
      top: 0
    })
  }

  tracker(NAVIGATION.ACTIONS.NAVIGATE_TO, NAVIGATION.LABELS.START)
}

const HeaderNavigation: React.FC<IProps> = ({
  activeNavigationItem,
  onClick,
  withSubheader
}) => {
  const { canShowNotifications, canShowGroupsList } = useGlobalPermissions()
  const unseenCount = usePollUnseenNotificationsCount()

  const {
    getEventsfeedRoute,
    getInfrastructureProjectsRoute,
    getPublicParticipationRoute,
    getNewsfeedRoute,
    getProfilesDirectoryRoute
  } = useContext(URLContext)

  const {
    mainPage,
    events,
    groups,
    infrastructure,
    notifications,
    profilesDirectory,
    publicParticipation
  } = textResources.header

  const features = useFeatureFlags()

  return (
    <div className={cls + '__wrapper'} onClick={onClick}>
      <hr className={cls + '__separation-line'} />
      <Tabs onClick={clickEvent} className={cls}>
        <FeatureFlag active={features.newsfeed}>
          <TabItemLink
            icon={'newsfeed'}
            isActive={activeNavigationItem === FEED_TYPE_NEWS}
            textLabel={mainPage}
            url={getNewsfeedRoute()}
            showLabelInMobile={!features.navigationHideLabelsInMobile}
          />
        </FeatureFlag>
        <FeatureFlag active={features.events}>
          <TabItemLink
            icon={'event'}
            isActive={activeNavigationItem === FEED_TYPE_EVENT}
            textLabel={events}
            url={getEventsfeedRoute()}
            showLabelInMobile={!features.navigationHideLabelsInMobile}
          />
        </FeatureFlag>
        <FeatureFlag active={features.publicParticipationProjects}>
          <TabItemLink
            icon={<PublicParticipationNavigationIcon />}
            isActive={activeNavigationItem === PUBLIC_PARTICIPATION}
            textLabel={publicParticipation}
            url={getPublicParticipationRoute()}
            showLabelInMobile={!features.navigationHideLabelsInMobile}
          />
        </FeatureFlag>
        <FeatureFlag active={features.infrastructureProjects}>
          <TabItemLink
            icon={<InfrastructureProjectsNavigationIcon />}
            isActive={activeNavigationItem === INFRASTRUCTURE_PROJECTS}
            textLabel={infrastructure}
            url={getInfrastructureProjectsRoute()}
            showLabelInMobile={!features.navigationHideLabelsInMobile}
          />
        </FeatureFlag>
        <FeatureFlag active={features.groups && canShowGroupsList}>
          <TabItemLink
            icon={'groups'}
            isActive={activeNavigationItem === GROUP_OVERVIEW}
            textLabel={groups}
            url={FRONTEND_GROUP_OVERVIEW_PATH}
            showLabelInMobile={!features.navigationHideLabelsInMobile}
          />
        </FeatureFlag>
        <FeatureFlag active={features.userDirectory}>
          <TabItemLink
            icon={'search-profile'}
            isActive={activeNavigationItem === PROFILES_DIRECTORY}
            textLabel={profilesDirectory}
            url={getProfilesDirectoryRoute()}
            showLabelInMobile={!features.navigationHideLabelsInMobile}
          />
        </FeatureFlag>
        <FeatureFlag active={features.notifications && canShowNotifications}>
          <TabItemLink
            badge
            icon={'notification'}
            isActive={activeNavigationItem === NOTIFICATIONS}
            isLocked={!canShowNotifications}
            badgeNumber={unseenCount}
            textLabel={notifications}
            url={FRONTEND_NOTIFICATIONS_PATH}
            showLabelInMobile={!features.navigationHideLabelsInMobile}
          />
        </FeatureFlag>
      </Tabs>
      {withSubheader && <hr className={cls + '__separation-line'} />}
    </div>
  )
}

/* set displayname to not have a component without a name */
HeaderNavigation.displayName = 'HeaderNavigation'

export default HeaderNavigation
